/**
 * Low-level read/write/delete primitives for manual cap table custom column values on a userClient record.
 *
 * Canonical storage: userClient[col.key]  (bare key, e.g. "budget_notes")
 * Legacy fallbacks (read-only, cleared on next write):
 *   userClient['ctcc_' + col.key]         (older prefix format)
 *   userClient[camelCase(col.label)]       (short-lived format, cleaned up on write)
 */

/** Internal: converts a column label to its legacy camelCase storage key. @param {string} str */
function capTableCustomColumnLabelToCamelCase(str) {
	if (!str || typeof str !== 'string') return '';
	const parts = String(str)
		.trim()
		.split(/[^a-zA-Z0-9]+/)
		.filter(Boolean)
		.map((w) => w.replace(/[^a-zA-Z0-9]/g, ''))
		.filter(Boolean);
	if (!parts.length) return '';
	const first = parts[0].toLowerCase();
	const rest = parts
		.slice(1)
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
		.join('');
	return first + rest;
}

/**
 * Property on userClient.clients[x] where manual cap table values are stored (= column key).
 * @param {{ key?: string }} col
 */
export function getCapTableCustomColumnClientStorageKey(col) {
	if (!col) return '';
	return String(col.key || '').trim();
}

/** Same rules as usesCtccKeyStorage (manual / non-resolver column). */
export function isManualCapTableCustomColumnSource(source) {
	const s = (source || '').trim();
	if (!s) return true;
	if (/^formula:/i.test(s) || /^aggregate:/i.test(s) || /^calculator:/i.test(s)) return false;
	if (s.includes(':')) return false;
	if (s.startsWith('userClient.')) return false;
	if (s.startsWith('user.')) return false;
	if (s.includes('.')) return false;
	return true;
}

export function usesCtccKeyStorage(col) {
	return isManualCapTableCustomColumnSource(col?.source);
}

function storedValuePresent(v) {
	if (v === undefined || v === null) return false;
	if (typeof v === 'string' && v.trim() === '') return false;
	return true;
}

/**
 * Read manual column value from userClient (column key, then legacy ctcc_*, then legacy camelCase(label)).
 * @param {object} userClient
 * @param {string} columnKey column definition key (e.g. effective_date)
 * @param {string} [columnLabel] optional; used only for legacy camelCase property migration
 */
export function readCapTableManualColumnFromUserClient(userClient, columnKey, columnLabel) {
	if (!userClient) return '';
	const k = String(columnKey || '').trim();
	let value = k ? userClient[k] : undefined;
	if (storedValuePresent(value)) return value;
	const legacyCtcc = k ? userClient['ctcc_' + k] : undefined;
	if (storedValuePresent(legacyCtcc)) return legacyCtcc;
	const legacyCamel = columnLabel ? capTableCustomColumnLabelToCamelCase(columnLabel) : '';
	if (legacyCamel && legacyCamel !== k) {
		const v = userClient[legacyCamel];
		if (storedValuePresent(v)) return v;
	}
	return '';
}

/** Remove column key property and legacy ctcc_* / camelCase(label) storage. */
export function deleteCapTableManualColumnStorageFromUserClient(userClient, col) {
	if (!userClient || !col) return;
	const k = String(col.key || '').trim();
	if (!k) return;
	if (Object.prototype.hasOwnProperty.call(userClient, k)) delete userClient[k];
	const legacyCtcc = 'ctcc_' + k;
	if (Object.prototype.hasOwnProperty.call(userClient, legacyCtcc)) delete userClient[legacyCtcc];
	const legacyCamel = capTableCustomColumnLabelToCamelCase(col.label || '');
	if (legacyCamel && legacyCamel !== k && Object.prototype.hasOwnProperty.call(userClient, legacyCamel)) {
		delete userClient[legacyCamel];
	}
}

/**
 * Persist manual column string on userClient[col.key]; clears legacy storages when migrating.
 * @param {object} userClient
 * @param {{ key?: string, label?: string }} col
 * @param {string} stringValue trimmed sanitized value (empty clears all storages for column)
 */
export function writeCapTableManualColumnToUserClient(userClient, col, stringValue) {
	if (!userClient || !col) return;
	const k = String(col.key || '').trim();
	if (!k) return;
	const trimmed = stringValue === undefined || stringValue === null ? '' : String(stringValue).trim();
	if (trimmed === '') {
		deleteCapTableManualColumnStorageFromUserClient(userClient, col);
		return;
	}
	userClient[k] = trimmed;
	const legacyCtcc = 'ctcc_' + k;
	if (Object.prototype.hasOwnProperty.call(userClient, legacyCtcc)) delete userClient[legacyCtcc];
	const legacyCamel = capTableCustomColumnLabelToCamelCase(col.label || '');
	if (legacyCamel && legacyCamel !== k && Object.prototype.hasOwnProperty.call(userClient, legacyCamel)) {
		delete userClient[legacyCamel];
	}
}
